import { useState, useCallback } from 'react';
import {
  handleQuickAddPlayer as playerQuickAdd,
  handleCreateProfile as playerCreateProfile,
  loadPlayers,
  syncPlayerProfile,
} from '../services/api/playerService';
import { apiClient } from '../services/api/secureApiClient';
import { isLocalStack } from '../constants/envConfig';
import { logger } from '../utils/logger';
import { isDemoMode } from '../utils/demoUtils';
import { mockDB } from '../services/mock/mockData';

const useMockData = process.env.REACT_APP_USE_MOCK_DATA === 'true';

export function usePlayerData(user, assignedName) {
  const [players, setPlayers] = useState([]);
  const [userProfile, setUserProfile] = useState(null);

  // Load players data
  const loadPlayersData = useCallback(async () => {
    logger.debug('loadPlayersData called, useMockData:', useMockData);

    // Demo Mode Logic: Return Client-Side Mock Data
    if (isDemoMode()) {
      logger.debug('Demo Mode: Loading mock players');
      const mockPlayers = mockDB.getPlayers();
      setPlayers(mockPlayers);
      // Auto-login as John Wick in demo mode
      const profile = mockPlayers.find(p => p.name === 'John Wick');
      setUserProfile(profile);
      return { players: mockPlayers, userProfile: profile };
    }

    if (useMockData) {
      logger.debug('Returning early due to useMockData');
      return;
    }

    try {
      logger.debug('About to call loadPlayers()');
      const playersResult = await loadPlayers();
      logger.debug('loadPlayers() returned:', playersResult);

      let profile = null;
      let finalPlayers = playersResult;

      // Priority: Real Auth0 user (even in LocalStack mode)
      if (user && user.sub && !user.sub.includes('localstack-user')) {
        // Real Auth0 user logged in
        logger.info('SECURITY: Auth0 user authenticated', {
          userId: user.sub,
          email: user.email,
          assignedName,
        });

        // 1. If user has an assigned name from whitelist, attempt Sync (Link/Create)
        if (assignedName) {
          logger.info('SECURITY: Attempting whitelist sync for assigned name:', assignedName);

          const { profile: syncedProfile, didUpdate } = await syncPlayerProfile(
            user,
            assignedName,
            playersResult
          );

          if (syncedProfile) {
            profile = syncedProfile;
            logger.info('SECURITY: Profile synced successfully', {
              profileId: profile.id,
              profileName: profile.name,
              profileEmail: profile.email,
              wasUpdated: didUpdate,
            });

            if (didUpdate && profile) {
              // Update local list to include new/updated player
              if (!playersResult.some(p => p.id === profile.id)) {
                finalPlayers = [...playersResult, profile];
              } else {
                finalPlayers = playersResult.map(p => p.id === profile.id ? profile : p);
              }
            }
          } else {
            // SECURITY: syncPlayerProfile failed
            logger.error('SECURITY ERROR: Profile sync failed for whitelisted user', {
              userId: user.sub,
              email: user.email,
              assignedName,
            });
          }
        }

        // 2. If no profile yet (no assigned name OR sync failed), try standard match by auth ID
        if (!profile) {
          const userId = user.sub;
          const userEmail = user.email;

          logger.info('SECURITY: Attempting standard profile match', {
            userId,
            userEmail,
          });

          profile = playersResult?.find(
            p =>
              (p.authUserId && p.authUserId === userId) ||
              (p.email && p.email === userEmail)
          );

          if (profile) {
            logger.info('SECURITY: Profile matched by auth credentials', {
              profileId: profile.id,
              profileName: profile.name,
              matchedBy: profile.authUserId === userId ? 'authUserId' : 'email',
            });
          } else {
            logger.error('SECURITY ERROR: No profile found for authenticated user', {
              userId,
              userEmail,
              assignedName,
            });
          }
        }

        // SECURITY CRITICAL: Verify profile belongs to this user
        if (profile) {
          const isValidProfile =
            profile.authUserId === user.sub ||
            profile.email?.toLowerCase() === user.email?.toLowerCase();

          if (!isValidProfile) {
            logger.error('SECURITY VIOLATION: Profile mismatch detected', {
              userId: user.sub,
              userEmail: user.email,
              profileId: profile.id,
              profileEmail: profile.email,
              profileAuthUserId: profile.authUserId,
            });
            // SECURITY: Clear profile to prevent unauthorized access
            profile = null;
          }
        }
      } else if (isLocalStack && !user) {
        // LocalStack dev mode without authentication
        // NO AUTO-LOGIN - user must click "Log In" button
        logger.debug('LocalStack: No authenticated user, leaving profile as null');
        profile = null;
      }

      setPlayers(finalPlayers);
      setUserProfile(profile);

      return { players: finalPlayers, userProfile: profile };
    } catch (error) {
      logger.error('Error loading players data:', error);
      return { players: [], userProfile: null };
    }
  }, [user, assignedName]);

  // Player operations
  const handleQuickAddPlayer = async name => {
    if (isDemoMode()) {
      const newPlayer = mockDB.addPlayer(name);
      setPlayers(prev => [...prev, newPlayer]);
      return newPlayer.id;
    }
    return await playerQuickAdd(name, players, setPlayers);
  };

  const handleCreateProfile = async e => {
    await playerCreateProfile(e, user, players, setPlayers, setUserProfile);
  };

  return {
    // State
    players,
    userProfile,
    setPlayers,
    setUserProfile,

    // Operations
    loadPlayersData,
    handleQuickAddPlayer,
    handleCreateProfile,
  };
}
